import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

import { useAuth } from '../../contextProviders/AuthProvider';
import Course, { CourseTee } from '../../types/Course';
import { exportTeesAsCSV, parseTeesFromCSV } from '../../utils/courseTeeCSV';
import { carryUsgaTeeSetRatingIds, isUsgaLinkedTee } from '../../utils/courseUtils';
import { PASSWORD_MANAGER_IGNORE_INPUT_PROPS } from '../../utils/formInputProps';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { generateFirestoreId } from '../../utils/utils';
import { readSpreadsheetFile, SPREADSHEET_FILE_ACCEPT } from '../../utils/utilsUploads';
import GratiColorPicker from '../common/GratiColorPicker';

import CourseTeeView from './CourseTeeView';

const HOLE_COUNT_OPTIONS = [9, 18];

type StrokeIndexField = 'mensStrokeIndex' | 'womensStrokeIndex';

const getDefaultPar = (tees: CourseTee[], holeCount: number): number[] => {
  for (const tee of tees) {
    if (tee.par?.some((v) => v > 0)) return [...tee.par];
  }
  return Array(holeCount).fill(0);
};

const getDefaultStrokeIndex = (tees: CourseTee[], field: StrokeIndexField, holeCount: number): number[] => {
  for (const tee of tees) {
    const values = tee[field];
    if (values?.some((v) => v > 0)) return [...values];
  }
  return Array(holeCount).fill(0);
};

const createEmptyTee = (holeCount: number, sortOrder: number, existingTees: CourseTee[]): CourseTee => ({
  itemId: generateFirestoreId(),
  name: '',
  mensRating: 0,
  mensSlope: 0,
  womensRating: 0,
  womensSlope: 0,
  yardage: Array(holeCount).fill(0),
  par: getDefaultPar(existingTees, holeCount),
  mensStrokeIndex: getDefaultStrokeIndex(existingTees, 'mensStrokeIndex', holeCount),
  womensStrokeIndex: getDefaultStrokeIndex(existingTees, 'womensStrokeIndex', holeCount),
  sortOrder,
  usgaMaleTeeSetRatingId: null,
  usgaFemaleTeeSetRatingId: null,
});

const sumRange = (arr: number[], start: number, end: number): number =>
  arr.slice(start, end).reduce((sum, val) => sum + val, 0);

interface CourseTeeEditorProps {
  course: Course;
  isAdmin?: boolean;
  onTeeDataChange: (updates: Partial<Course>) => void;
}

export default function CourseTeeEditor(props: CourseTeeEditorProps): ReactElement {
  const { course, isAdmin = true, onTeeDataChange } = props;
  // USGA tee set ids are God-only — Firestore rules refuse anyone else's change to them.
  const { isGod } = useAuth();

  const tees = useMemo(() => course.tees ?? [], [course.tees]);
  const sortedTees = useMemo(
    () => [...tees].sort((a, b) => {
      const yA = a.yardage?.reduce((s, y) => s + y, 0) ?? 0;
      const yB = b.yardage?.reduce((s, y) => s + y, 0) ?? 0;
      return yB - yA;
    }),
    [tees],
  );

  const duplicateTeeNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const t of sortedTees) {
      const key = t.name.toLowerCase();
      if (seen.has(key)) dupes.add(key);
      else seen.add(key);
    }
    return dupes;
  }, [sortedTees]);

  const teeLabel = (tee: CourseTee) => {
    if (!duplicateTeeNames.has(tee.name.toLowerCase())) return tee.name;
    if (tee.isForMen && tee.isForWomen) return `${tee.name} (Men & Women)`;
    if (tee.isForMen) return `${tee.name} (Men)`;
    if (tee.isForWomen) return `${tee.name} (Women)`;
    return tee.name;
  };

  const [holeCount, setHoleCount] = useState<number>(() =>
    course.tees?.length ? (course.tees[0]?.yardage?.length ?? 18) : 18
  );

  useEffect(() => {
    if (course.tees?.length) {
      setHoleCount(course.tees[0]?.yardage?.length ?? 18);
    }
  }, [course.tees]);

  const is18 = holeCount === 18;

  const isSplit = useMediaQuery('(max-width: 1024px)');

  const [editingTeeId, setEditingTeeId] = useState<string | null>(null);
  const [deletingTeeId, setDeletingTeeId] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<CourseTee[] | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadCSV = useCallback(() => {
    exportTeesAsCSV(tees, holeCount, course.name || 'course');
  }, [tees, holeCount, course.name]);

  const handleImportFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;

    readSpreadsheetFile(file).then((text) => {
      const { tees: parsed, errors } = parseTeesFromCSV(text);
      if (errors.length > 0 && parsed.length === 0) {
        setImportErrors(errors);
        return;
      }
      let toImport = parsed;
      if (!isGod) {
        const { tees: carried, droppedLinkedTeeNames } = carryUsgaTeeSetRatingIds(tees, parsed);
        if (droppedLinkedTeeNames.length > 0) {
          const one = droppedLinkedTeeNames.length === 1;
          setImportErrors([
            `The file doesn't include ${droppedLinkedTeeNames.join(', ')}, which ${one ? 'is' : 'are'} linked to the USGA. Only a Gratigolf admin can remove ${one ? 'it' : 'them'}.`,
          ]);
          return;
        }
        toImport = carried;
      }
      setImportErrors(errors);
      if (tees.length > 0) {
        setPendingImport(toImport);
      } else {
        onTeeDataChange({ tees: toImport });
      }
    });
  }, [tees, isGod, onTeeDataChange]);

  const handleConfirmImport = useCallback(() => {
    if (pendingImport) {
      onTeeDataChange({ tees: pendingImport });
      setPendingImport(null);
      setEditingTeeId(null);
    }
  }, [pendingImport, onTeeDataChange]);

  const handleCancelImport = useCallback(() => {
    setPendingImport(null);
    setImportErrors([]);
  }, []);

  const updateTees = useCallback((newTees: CourseTee[]) => {
    onTeeDataChange({ tees: newTees });
  }, [onTeeDataChange]);

  const handleHoleCountChange = useCallback((newCount: number) => {
    if (newCount === holeCount) return;
    setHoleCount(newCount);
    if (tees.length > 0) {
      const resized = tees.map((tee) => ({
        ...tee,
        yardage: resizeArray(tee.yardage, newCount, 0),
        par: resizeArray(tee.par, newCount, 0),
        mensStrokeIndex: resizeArray(tee.mensStrokeIndex, newCount, 0),
        womensStrokeIndex: resizeArray(tee.womensStrokeIndex, newCount, 0),
        ...(tee.holeColors ? { holeColors: resizeArray(tee.holeColors, newCount, '') } : {}),
      }));
      onTeeDataChange({ tees: resized });
    }
  }, [holeCount, tees, onTeeDataChange]);

  const handleAddTee = useCallback(() => {
    const newTee = createEmptyTee(holeCount, tees.length, tees);
    updateTees([...tees, newTee]);
    setEditingTeeId(newTee.itemId);
    setDeletingTeeId(null);
  }, [tees, holeCount, updateTees]);

  const handleRemoveTee = useCallback((teeId: string) => {
    const newTees = tees
      .filter((t) => t.itemId !== teeId)
      .map((t, i) => ({ ...t, sortOrder: i }));
    updateTees(newTees);
    setEditingTeeId(null);
    setDeletingTeeId(null);
  }, [tees, updateTees]);

  const handleTeeFieldChange = useCallback(<K extends keyof CourseTee>(
    teeId: string, field: K, value: CourseTee[K],
  ) => {
    const newTees = tees.map((tee) => tee.itemId === teeId ? { ...tee, [field]: value } : tee);
    updateTees(newTees);
  }, [tees, updateTees]);

  const handleHoleValueChange = useCallback((
    teeId: string,
    field: 'yardage' | 'par' | 'mensStrokeIndex' | 'womensStrokeIndex',
    holeIndex: number,
    value: number,
  ) => {
    const tee = tees.find((t) => t.itemId === teeId);
    if (!tee) return;
    const newArr = [...tee[field]];
    newArr[holeIndex] = value;
    handleTeeFieldChange(teeId, field, newArr);
  }, [tees, handleTeeFieldChange]);

  const handleHoleColorChange = useCallback((teeId: string, holeIndex: number, color: string) => {
    const tee = tees.find((t) => t.itemId === teeId);
    if (!tee) return;
    const newArr = tee.holeColors ? [...tee.holeColors] : Array(holeCount).fill('');
    newArr[holeIndex] = color;
    handleTeeFieldChange(teeId, 'holeColors', newArr);
  }, [tees, holeCount, handleTeeFieldChange]);

  const [comboColorSnackbar, setComboColorSnackbar] = useState<string | null>(null);

  const handleSetComboColors = useCallback((tee: CourseTee) => {
    // Only consider tees whose color is actually one of this combo's own two colors —
    // otherwise an unrelated tee that happens to share a hole's yardage (e.g. a "Gold"
    // tee coincidentally matching a "Blue/White" combo on one hole) creates a false
    // ambiguity and blocks a real, unambiguous match.
    const otherTees = tees.filter((t) =>
      t.itemId !== tee.itemId && !t.combo && t.color && (t.color === tee.color || t.color === tee.color2)
    );
    const newHoleColors: string[] = [];
    for (let i = 0; i < tee.yardage.length; i++) {
      const matches = otherTees.filter((t) => t.yardage[i] === tee.yardage[i]);
      newHoleColors[i] = matches.length === 1 ? (matches[0].color as string) : '';
    }
    handleTeeFieldChange(tee.itemId, 'holeColors', newHoleColors);
    const setCount = newHoleColors.filter((c) => c).length;
    const totalCount = newHoleColors.length;
    setComboColorSnackbar(
      `${setCount} of ${totalCount} holes set from matching tees; ${totalCount - setCount} left unset`
    );
  }, [tees, handleTeeFieldChange]);

  type HoleColorMode = 'TEE' | 'HOLE' | 'SIDE';

  const [holeColorModeOverrides, setHoleColorModeOverrides] = useState<Record<string, HoleColorMode>>({});

  const getHoleColorMode = (tee: CourseTee): HoleColorMode =>
    holeColorModeOverrides[tee.itemId] ?? (tee.holeColors?.some((c) => c) ? 'HOLE' : 'TEE');

  const handleHoleColorModeChange = useCallback((teeId: string, mode: HoleColorMode) => {
    setHoleColorModeOverrides((prev) => ({ ...prev, [teeId]: mode }));
  }, []);

  const handleResetHoleColors = useCallback((tee: CourseTee) => {
    const mode = holeColorModeOverrides[tee.itemId] ?? (tee.holeColors?.some((c) => c) ? 'HOLE' : 'TEE');
    if (mode === 'HOLE') {
      handleSetComboColors(tee);
    } else if (mode === 'SIDE') {
      const newArr = Array(holeCount).fill('');
      for (let i = 0; i < holeCount; i++) {
        newArr[i] = i < 9 ? (tee.color || '') : (tee.color2 || '');
      }
      handleTeeFieldChange(tee.itemId, 'holeColors', newArr);
      setComboColorSnackbar('Front 9 and Back 9 colors set.');
    } else {
      handleTeeFieldChange(tee.itemId, 'holeColors', []);
      setComboColorSnackbar('Cleared — using a single color for the whole tee.');
    }
  }, [holeColorModeOverrides, holeCount, handleTeeFieldChange, handleSetComboColors]);

  const cellSx = {
    px: '0.25rem',
    py: '0.125rem',
    minWidth: '2.5rem',
    borderRight: '1px solid',
    borderColor: 'divider',
  };

  const inputSx = {
    '& .MuiInputBase-input': {
      textAlign: 'center',
      py: '0.25rem',
      px: '0.125rem',
      fontSize: '0.875rem',
    },
  };

  const subtotalCellSx = {
    ...cellSx,
    fontWeight: 'bold',
    backgroundColor: 'action.hover',
  };

  return (
    <Stack spacing={2}>
      {/* Toolbar */}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1
        }}>
        <Typography variant="subtitle1" sx={{
          fontWeight: "bold"
        }}>Tee Data</Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            flexWrap: "wrap"
          }}>
          <TextField
            select
            size="small"
            label="Holes"
            value={holeCount}
            onChange={({ target }) => handleHoleCountChange(parseInt(target.value, 10))}
            sx={{ minWidth: '5rem' }}
          >
            {HOLE_COUNT_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          {isAdmin && (
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddTee}>
              Add Tee
            </Button>
          )}
          {tees.length > 0 && (
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>
              Download CSV file
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={SPREADSHEET_FILE_ACCEPT}
            style={{ display: 'none' }}
            onChange={handleImportFileChange}
          />
          {isAdmin && (
            <Button size="small" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
              Import CSV or XLSX file
            </Button>
          )}
        </Stack>
      </Stack>
      {importErrors.length > 0 && (
        <Alert severity="error" onClose={() => setImportErrors([])}>
          {importErrors.join(' ')}
        </Alert>
      )}
      {pendingImport && (
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" color="warning" onClick={handleConfirmImport}>
                Replace
              </Button>
              <Button size="small" onClick={handleCancelImport}>
                Cancel
              </Button>
            </Stack>
          }
        >
          This will replace {tees.length} existing tee{tees.length !== 1 ? 's' : ''} with {pendingImport.length} imported tee{pendingImport.length !== 1 ? 's' : ''}.
        </Alert>
      )}
      {/* Default tee selectors */}
      {isAdmin && tees.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ minWidth: '6rem', fontWeight: 'bold' }}>Defaults</Typography>
          <TextField
            select
            size="small"
            label="Default (no gender)"
            value={course.defaultTeeId ?? ''}
            onChange={({ target }) => { if (target.value) onTeeDataChange({ defaultTeeId: target.value }); }}
            sx={{ minWidth: '10rem' }}
          >
            {sortedTees.map((tee) => (
              <MenuItem key={tee.itemId} value={tee.itemId}>{teeLabel(tee)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Default Men's"
            value={course.defaultMensTeeId ?? ''}
            onChange={({ target }) => { if (target.value) onTeeDataChange({ defaultMensTeeId: target.value }); }}
            sx={{ minWidth: '10rem' }}
          >
            {sortedTees.filter((t) => t.isForMen !== false).map((tee) => (
              <MenuItem key={tee.itemId} value={tee.itemId}>{teeLabel(tee)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Default Women's"
            value={course.defaultWomensTeeId ?? ''}
            onChange={({ target }) => { if (target.value) onTeeDataChange({ defaultWomensTeeId: target.value }); }}
            sx={{ minWidth: '10rem' }}
          >
            {sortedTees.filter((t) => t.isForWomen !== false).map((tee) => (
              <MenuItem key={tee.itemId} value={tee.itemId}>{teeLabel(tee)}</MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      {/* Tee list */}
      {sortedTees.map((tee) =>
        editingTeeId === tee.itemId ? (
          /* ── Edit mode ── */
          (<Box key={tee.itemId} sx={{ border: 1, borderColor: 'primary.main', borderRadius: 1, p: 1 }}>
          <Stack spacing={2}>
            {/* Edit header: colors, name, combo, Done, Delete */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center"
              }}>
              <GratiColorPicker
                value={tee.color || undefined}
                defaultValue="#cccccc"
                onChange={(color) => handleTeeFieldChange(tee.itemId, 'color', color)}
              />
              {tee.combo && (
                <GratiColorPicker
                  value={tee.color2 || undefined}
                  defaultValue="#cccccc"
                  onChange={(color) => handleTeeFieldChange(tee.itemId, 'color2', color)}
                />
              )}
              <TextField
                size="small"
                label="Tee Name"
                slotProps={{ htmlInput: PASSWORD_MANAGER_IGNORE_INPUT_PROPS }}
                value={tee.name}
                onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'name', target.value)}
                sx={{ minWidth: '6rem', flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!tee.combo}
                    onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'combo', target.checked)}
                  />
                }
                label="Combo"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={tee.isForMen !== false}
                    onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'isForMen', target.checked)}
                  />
                }
                label="Men"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={tee.isForWomen !== false}
                    onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'isForWomen', target.checked)}
                  />
                }
                label="Women"
                sx={{ whiteSpace: 'nowrap' }}
              />

              {/* Delete / confirm */}
              {deletingTeeId === tee.itemId && !isGod && isUsgaLinkedTee(tee) ? (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Typography variant="bodySmall" color="text.secondary">
                    Linked to the USGA. Only a Gratigolf admin can remove it.
                  </Typography>
                  <Button size="small" onClick={() => setDeletingTeeId(null)}>
                    OK
                  </Button>
                </Stack>
              ) : deletingTeeId === tee.itemId ? (
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" color="error" onClick={() => handleRemoveTee(tee.itemId)}>
                    Confirm
                  </Button>
                  <Button size="small" onClick={() => setDeletingTeeId(null)}>
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeletingTeeId(tee.itemId)}
                  aria-label={`Delete ${tee.name || 'tee'}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}

              {/* Done button */}
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={() => { setEditingTeeId(null); setDeletingTeeId(null); }}
              >
                Done
              </Button>
            </Stack>
            {tee.combo && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Hole color by</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={getHoleColorMode(tee)}
                  onChange={(_e, mode: HoleColorMode | null) => { if (mode) handleHoleColorModeChange(tee.itemId, mode); }}
                >
                  <ToggleButton value="TEE">Tee</ToggleButton>
                  <ToggleButton value="HOLE">Hole</ToggleButton>
                  {is18 && <ToggleButton value="SIDE">Side</ToggleButton>}
                </ToggleButtonGroup>
                <Button size="small" onClick={() => handleResetHoleColors(tee)}>
                  Reset
                </Button>
              </Stack>
            )}
            {/* Ratings */}
            <Stack spacing={1}>
              {tee.isForMen !== false && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}>
                  <Typography variant="body2" sx={{ minWidth: '4.5rem' }}>{"Men's"}</Typography>
                  <DecimalInput
                    label="Rating"
                    value={tee.mensRating}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'mensRating', val)}
                    sx={{ width: '5rem' }}
                  />
                  <TextField
                    size="small"
                    label="Slope"
                    slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric', pattern: '[0-9]*' } }}
                    value={tee.mensSlope || ''}
                    onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'mensSlope', parseInt(target.value, 10) || 0)}
                    sx={{ width: '5rem' }}
                  />
                  {is18 && (<>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>F9</Typography>
                  <OptionalDecimalInput
                    label="Rating"
                    value={tee.mensRatingFront9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'mensRatingFront9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <OptionalSlopeInput
                    label="Slope"
                    value={tee.mensSlopeFront9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'mensSlopeFront9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>B9</Typography>
                  <OptionalDecimalInput
                    label="Rating"
                    value={tee.mensRatingBack9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'mensRatingBack9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <OptionalSlopeInput
                    label="Slope"
                    value={tee.mensSlopeBack9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'mensSlopeBack9', val)}
                    sx={{ width: '5rem' }}
                  /></>)}
                  {isGod && (
                    <OptionalSlopeInput
                      label={HANDICAP_LABELS.TEE_SET_RATING_ID}
                      value={tee.usgaMaleTeeSetRatingId ?? undefined}
                      onChange={(val) => handleTeeFieldChange(tee.itemId, 'usgaMaleTeeSetRatingId', val ?? null)}
                      sx={{ width: '10rem' }}
                    />
                  )}
                </Stack>
              )}
              {tee.isForWomen !== false && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}>
                  <Typography variant="body2" sx={{ minWidth: '4.5rem' }}>{"Women's"}</Typography>
                  <DecimalInput
                    label="Rating"
                    value={tee.womensRating}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'womensRating', val)}
                    sx={{ width: '5rem' }}
                  />
                  <TextField
                    size="small"
                    label="Slope"
                    slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric', pattern: '[0-9]*' } }}
                    value={tee.womensSlope || ''}
                    onChange={({ target }) => handleTeeFieldChange(tee.itemId, 'womensSlope', parseInt(target.value, 10) || 0)}
                    sx={{ width: '5rem' }}
                  />
                  {is18 && (<>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>F9</Typography>
                  <OptionalDecimalInput
                    label="Rating"
                    value={tee.womensRatingFront9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'womensRatingFront9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <OptionalSlopeInput
                    label="Slope"
                    value={tee.womensSlopeFront9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'womensSlopeFront9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>B9</Typography>
                  <OptionalDecimalInput
                    label="Rating"
                    value={tee.womensRatingBack9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'womensRatingBack9', val)}
                    sx={{ width: '5rem' }}
                  />
                  <OptionalSlopeInput
                    label="Slope"
                    value={tee.womensSlopeBack9}
                    onChange={(val) => handleTeeFieldChange(tee.itemId, 'womensSlopeBack9', val)}
                    sx={{ width: '5rem' }}
                  /></>)}
                  {isGod && (
                    <OptionalSlopeInput
                      label={HANDICAP_LABELS.TEE_SET_RATING_ID}
                      value={tee.usgaFemaleTeeSetRatingId ?? undefined}
                      onChange={(val) => handleTeeFieldChange(tee.itemId, 'usgaFemaleTeeSetRatingId', val ?? null)}
                      sx={{ width: '10rem' }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
            {/* Hole-by-hole table */}
            {is18 && isSplit ? (
              <Stack spacing={1}>
                {/* Front 9 */}
                <TableContainer>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...cellSx, minWidth: '3.5rem', fontWeight: 'bold', whiteSpace: 'normal' }}>Hole</TableCell>
                        {Array.from({ length: 9 }, (_, i) => (
                          <TableCell key={i} align="center" sx={cellSx}>{i + 1}</TableCell>
                        ))}
                        <TableCell align="center" sx={subtotalCellSx}>Out</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <HoleDataRow label="Yards" values={tee.yardage} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'yardage', holeIdx, val)} maxDigits={3} rangeStart={0} rangeEnd={9} showRangeSubtotal />
                      {tee.combo && (
                        <HoleColorRow label="Hole Colors" values={tee.holeColors ?? []} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} onChange={(holeIdx, color) => handleHoleColorChange(tee.itemId, holeIdx, color)} rangeStart={0} rangeEnd={9} showRangeSubtotal />
                      )}
                      <HoleDataRow label="Par" values={tee.par} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'par', holeIdx, val)} maxDigits={1} rangeStart={0} rangeEnd={9} showRangeSubtotal />
                      {tee.isForMen !== false && (
                        <HoleDataRow label="Men's Hdcp" values={tee.mensStrokeIndex} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'mensStrokeIndex', holeIdx, val)} hideSubtotals maxDigits={2} rangeStart={0} rangeEnd={9} showRangeSubtotal />
                      )}
                      {tee.isForWomen !== false && (
                        <HoleDataRow label="Women's Hdcp" values={tee.womensStrokeIndex} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'womensStrokeIndex', holeIdx, val)} hideSubtotals maxDigits={2} rangeStart={0} rangeEnd={9} showRangeSubtotal />
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Back 9 */}
                <TableContainer>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...cellSx, minWidth: '3.5rem', fontWeight: 'bold', whiteSpace: 'normal' }}>Hole</TableCell>
                        {Array.from({ length: 9 }, (_, i) => (
                          <TableCell key={i + 9} align="center" sx={cellSx}>{i + 10}</TableCell>
                        ))}
                        <TableCell align="center" sx={subtotalCellSx}>In</TableCell>
                        <TableCell align="center" sx={subtotalCellSx}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <HoleDataRow label="Yards" values={tee.yardage} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'yardage', holeIdx, val)} maxDigits={3} rangeStart={9} rangeEnd={18} showRangeSubtotal showGrandTotal />
                      {tee.combo && (
                        <HoleColorRow label="Hole Colors" values={tee.holeColors ?? []} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} onChange={(holeIdx, color) => handleHoleColorChange(tee.itemId, holeIdx, color)} rangeStart={9} rangeEnd={18} showRangeSubtotal showGrandTotal />
                      )}
                      <HoleDataRow label="Par" values={tee.par} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'par', holeIdx, val)} maxDigits={1} rangeStart={9} rangeEnd={18} showRangeSubtotal showGrandTotal />
                      {tee.isForMen !== false && (
                        <HoleDataRow label="Men's Hdcp" values={tee.mensStrokeIndex} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'mensStrokeIndex', holeIdx, val)} hideSubtotals maxDigits={2} rangeStart={9} rangeEnd={18} showRangeSubtotal showGrandTotal />
                      )}
                      {tee.isForWomen !== false && (
                        <HoleDataRow label="Women's Hdcp" values={tee.womensStrokeIndex} holeCount={holeCount} cellSx={cellSx} subtotalCellSx={subtotalCellSx} inputSx={inputSx} onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'womensStrokeIndex', holeIdx, val)} hideSubtotals maxDigits={2} rangeStart={9} rangeEnd={18} showRangeSubtotal showGrandTotal />
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...cellSx, minWidth: '3.5rem', fontWeight: 'bold', whiteSpace: 'normal' }}>Hole</TableCell>
                      {Array.from({ length: is18 ? 9 : holeCount }, (_, i) => (
                        <TableCell key={i} align="center" sx={cellSx}>{i + 1}</TableCell>
                      ))}
                      <TableCell align="center" sx={subtotalCellSx}>Out</TableCell>
                      {is18 && (
                        <>
                          {Array.from({ length: 9 }, (_, i) => (
                            <TableCell key={i + 9} align="center" sx={cellSx}>{i + 10}</TableCell>
                          ))}
                          <TableCell align="center" sx={subtotalCellSx}>In</TableCell>
                          <TableCell align="center" sx={subtotalCellSx}>Total</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <HoleDataRow
                      label="Yards"
                      values={tee.yardage}
                      holeCount={holeCount}
                      cellSx={cellSx}
                      subtotalCellSx={subtotalCellSx}
                      inputSx={inputSx}
                      onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'yardage', holeIdx, val)}
                      maxDigits={3}
                    />
                    {tee.combo && (
                      <HoleColorRow
                        label="Hole Colors"
                        values={tee.holeColors ?? []}
                        holeCount={holeCount}
                        cellSx={cellSx}
                        subtotalCellSx={subtotalCellSx}
                        onChange={(holeIdx, color) => handleHoleColorChange(tee.itemId, holeIdx, color)}
                      />
                    )}
                    <HoleDataRow
                      label="Par"
                      values={tee.par}
                      holeCount={holeCount}
                      cellSx={cellSx}
                      subtotalCellSx={subtotalCellSx}
                      inputSx={inputSx}
                      onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'par', holeIdx, val)}
                      maxDigits={1}
                    />
                    {tee.isForMen !== false && (
                      <HoleDataRow
                        label="Men's Hdcp"
                        values={tee.mensStrokeIndex}
                        holeCount={holeCount}
                        cellSx={cellSx}
                        subtotalCellSx={subtotalCellSx}
                        inputSx={inputSx}
                        onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'mensStrokeIndex', holeIdx, val)}
                        hideSubtotals
                        maxDigits={2}
                      />
                    )}
                    {tee.isForWomen !== false && (
                      <HoleDataRow
                        label="Women's Hdcp"
                        values={tee.womensStrokeIndex}
                        holeCount={holeCount}
                        cellSx={cellSx}
                        subtotalCellSx={subtotalCellSx}
                        inputSx={inputSx}
                        onChange={(holeIdx, val) => handleHoleValueChange(tee.itemId, 'womensStrokeIndex', holeIdx, val)}
                        hideSubtotals
                        maxDigits={2}
                      />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
          </Box>)
        ) : (
          /* ── View mode ── */
          (<CourseTeeView
            key={tee.itemId}
            tee={tee}
            isAdmin={isAdmin}
            onEdit={() => { setEditingTeeId(tee.itemId); setDeletingTeeId(null); }}
          />)
        )
      )}
      {tees.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            py: 2,
            textAlign: 'center'
          }}>
          No tees configured. Click &quot;Add Tee&quot; to add tee data for this course.
        </Typography>
      )}
      <Snackbar
        open={!!comboColorSnackbar}
        autoHideDuration={4000}
        onClose={() => setComboColorSnackbar(null)}
        message={comboColorSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}

// --- Helper components ---

interface HoleDataRowProps {
  label: string;
  values: number[];
  holeCount: number;
  cellSx: object;
  subtotalCellSx: object;
  inputSx: object;
  onChange: (holeIndex: number, value: number) => void;
  hideSubtotals?: boolean;
  maxDigits?: number;
  rangeStart?: number;
  rangeEnd?: number;
  showRangeSubtotal?: boolean;
  showGrandTotal?: boolean;
}

function HoleDataRow(props: HoleDataRowProps): ReactElement {
  const { label, values, holeCount, cellSx, subtotalCellSx, inputSx, onChange, hideSubtotals, maxDigits, rangeStart, rangeEnd, showRangeSubtotal, showGrandTotal } = props;
  const is18 = holeCount === 18;
  const frontCount = is18 ? 9 : holeCount;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (holeIndex: number, rawValue: string) => {
    const value = parseInt(rawValue, 10) || 0;
    onChange(holeIndex, value);
    const advanceLimit = rangeEnd ?? holeCount;
    if (maxDigits && rawValue.length >= maxDigits && holeIndex < advanceLimit - 1) {
      const nextInput = inputRefs.current[holeIndex + 1];
      if (nextInput) {
        setTimeout(() => {
          nextInput.focus();
          nextInput.select();
        }, 0);
      }
    }
  };

  const setInputRef = (holeIndex: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[holeIndex] = el;
  };

  const labelCellSx = { ...cellSx, fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'normal', lineHeight: 1.3 };

  if (rangeStart !== undefined) {
    const end = rangeEnd ?? holeCount;
    return (
      <TableRow>
        <TableCell sx={labelCellSx}>{label}</TableCell>
        {Array.from({ length: end - rangeStart }, (_, i) => {
          const holeIndex = rangeStart + i;
          return (
            <TableCell key={holeIndex} align="center" sx={cellSx}>
              <TextField
                variant="standard"
                size="small"
                value={values[holeIndex] || ''}
                slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, ref: setInputRef(holeIndex), inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': `${label} hole ${holeIndex + 1}` } }}
                onChange={({ target }) => handleChange(holeIndex, target.value)}
                sx={inputSx}
              />
            </TableCell>
          );
        })}
        {showRangeSubtotal && (
          <TableCell align="center" sx={subtotalCellSx}>
            {hideSubtotals ? '' : sumRange(values, rangeStart, end) || ''}
          </TableCell>
        )}
        {showGrandTotal && (
          <TableCell align="center" sx={subtotalCellSx}>
            {hideSubtotals ? '' : sumRange(values, 0, holeCount) || ''}
          </TableCell>
        )}
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell sx={labelCellSx}>{label}</TableCell>
      {Array.from({ length: frontCount }, (_, i) => (
        <TableCell key={i} align="center" sx={cellSx}>
          <TextField
            variant="standard"
            size="small"
            value={values[i] || ''}
            slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, ref: setInputRef(i), inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': `${label} hole ${i + 1}` } }}
            onChange={({ target }) => handleChange(i, target.value)}
            sx={inputSx}
          />
        </TableCell>
      ))}
      <TableCell align="center" sx={subtotalCellSx}>
        {hideSubtotals ? '' : sumRange(values, 0, frontCount) || ''}
      </TableCell>
      {is18 && (
        <>
          {Array.from({ length: 9 }, (_, i) => (
            <TableCell key={i + 9} align="center" sx={cellSx}>
              <TextField
                variant="standard"
                size="small"
                value={values[i + 9] || ''}
                slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, ref: setInputRef(i + 9), inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': `${label} hole ${i + 10}` } }}
                onChange={({ target }) => handleChange(i + 9, target.value)}
                sx={inputSx}
              />
            </TableCell>
          ))}
          <TableCell align="center" sx={subtotalCellSx}>
            {hideSubtotals ? '' : sumRange(values, 9, 18) || ''}
          </TableCell>
          <TableCell align="center" sx={subtotalCellSx}>
            {hideSubtotals ? '' : sumRange(values, 0, 18) || ''}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

interface HoleColorRowProps {
  label: string;
  values: string[];
  holeCount: number;
  cellSx: object;
  subtotalCellSx: object;
  onChange: (holeIndex: number, color: string) => void;
  rangeStart?: number;
  rangeEnd?: number;
  showRangeSubtotal?: boolean;
  showGrandTotal?: boolean;
}

function HoleColorRow(props: HoleColorRowProps): ReactElement {
  const { label, values, holeCount, cellSx, subtotalCellSx, onChange, rangeStart, rangeEnd, showRangeSubtotal, showGrandTotal } = props;
  const is18 = holeCount === 18;
  const frontCount = is18 ? 9 : holeCount;

  const labelCellSx = { ...cellSx, fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'normal', lineHeight: 1.3 };

  if (rangeStart !== undefined) {
    const end = rangeEnd ?? holeCount;
    return (
      <TableRow>
        <TableCell sx={labelCellSx}>{label}</TableCell>
        {Array.from({ length: end - rangeStart }, (_, i) => {
          const holeIndex = rangeStart + i;
          return (
            <TableCell key={holeIndex} align="center" sx={cellSx}>
              <GratiColorPicker
                value={values[holeIndex] || undefined}
                defaultValue="#cccccc"
                onChange={(color) => onChange(holeIndex, color)}
                sx={{ width: '1.25rem', height: '1.25rem' }}
              />
            </TableCell>
          );
        })}
        {showRangeSubtotal && <TableCell align="center" sx={subtotalCellSx} />}
        {showGrandTotal && <TableCell align="center" sx={subtotalCellSx} />}
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell sx={labelCellSx}>{label}</TableCell>
      {Array.from({ length: frontCount }, (_, i) => (
        <TableCell key={i} align="center" sx={cellSx}>
          <GratiColorPicker
            value={values[i] || undefined}
            defaultValue="#cccccc"
            onChange={(color) => onChange(i, color)}
            sx={{ width: '1.25rem', height: '1.25rem' }}
          />
        </TableCell>
      ))}
      <TableCell align="center" sx={subtotalCellSx} />
      {is18 && (
        <>
          {Array.from({ length: 9 }, (_, i) => (
            <TableCell key={i + 9} align="center" sx={cellSx}>
              <GratiColorPicker
                value={values[i + 9] || undefined}
                defaultValue="#cccccc"
                onChange={(color) => onChange(i + 9, color)}
                sx={{ width: '1.25rem', height: '1.25rem' }}
              />
            </TableCell>
          ))}
          <TableCell align="center" sx={subtotalCellSx} />
          <TableCell align="center" sx={subtotalCellSx} />
        </>
      )}
    </TableRow>
  );
}

const formatRating = (value: number): string => {
  const s = String(value);
  return s.includes('.') ? s : `${s}.0`;
};

interface OptionalDecimalInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  sx?: object;
}

function OptionalDecimalInput(props: OptionalDecimalInputProps): ReactElement {
  const { value, onChange, label, sx } = props;
  const [raw, setRaw] = useState(value != null ? formatRating(value) : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setRaw(value != null ? formatRating(value) : '');
    }
  }, [value, focused]);

  return (
    <TextField
      size="small"
      label={label}
      slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'decimal', step: '0.1' } }}
      value={raw}
      onChange={({ target }) => {
        setRaw(target.value);
        const parsed = parseFloat(target.value);
        if (!isNaN(parsed)) onChange(parsed);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const parsed = parseFloat(raw);
        onChange(isNaN(parsed) ? undefined : parsed);
      }}
      sx={sx}
    />
  );
}

interface OptionalSlopeInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  sx?: object;
}

function OptionalSlopeInput(props: OptionalSlopeInputProps): ReactElement {
  const { value, onChange, label, sx } = props;
  return (
    <TextField
      size="small"
      label={label}
      slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'numeric', pattern: '[0-9]*' } }}
      value={value ?? ''}
      onChange={({ target }) => {
        const parsed = parseInt(target.value, 10);
        onChange(isNaN(parsed) ? undefined : parsed);
      }}
      sx={sx}
    />
  );
}

interface DecimalInputProps {
  value: number;
  onChange: (value: number) => void;
  label: string;
  sx?: object;
}

function DecimalInput(props: DecimalInputProps): ReactElement {
  const { value, onChange, label, sx } = props;
  const [raw, setRaw] = useState(value ? formatRating(value) : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setRaw(value ? formatRating(value) : '');
    }
  }, [value, focused]);

  return (
    <TextField
      size="small"
      label={label}
      slotProps={{ htmlInput: { ...PASSWORD_MANAGER_IGNORE_INPUT_PROPS, inputMode: 'decimal', step: '0.1' } }}
      value={raw}
      onChange={({ target }) => {
        setRaw(target.value);
        const parsed = parseFloat(target.value);
        if (!isNaN(parsed)) onChange(parsed);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const parsed = parseFloat(raw);
        onChange(isNaN(parsed) ? 0 : parsed);
      }}
      sx={sx}
    />
  );
}

// --- Utilities ---

function resizeArray<T>(arr: T[], newLength: number, fill: T): T[] {
  if (arr.length === newLength) return arr;
  if (arr.length > newLength) return arr.slice(0, newLength);
  return [...arr, ...Array(newLength - arr.length).fill(fill)];
}
